import { Badge, Button, Card, FormField, Input, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ConnectorResponse } from "@/api";
import {
  clearConnectorSecretMutation,
  clearMyConnectorCredentialMutation,
  errorMessage,
  getConnectorDetailQueryKey,
  listConnectorsQueryKey,
  listConnectorToolsQueryKey,
  setConnectorSecretMutation,
  setMyConnectorCredentialMutation,
} from "@/api";
import { Text } from "@/components/Text";

/**
 * The credential this connector sends. Shared key: its admins set one key for everyone.
 * Per person: each person adds their own token. Values are write-only, never shown.
 */
export function ConnectorCredentialsCard({ connector }: Readonly<{ connector: ConnectorResponse }>) {
  const { t } = useTranslation("connectors");
  const toast = useToast();
  const queryClient = useQueryClient();
  const inputId = useId();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const path = { connector_uuid: connector.uuid };
  // OAuth connectors (phase 6): the connector's secret is its sign-in app's client secret.
  const oauth = connector.authMode === "oauth";
  const shared = connector.authMode === "shared_secret" || oauth;
  const isSet = shared ? connector.hasSecret : connector.hasMyCredential;
  const canSet = !shared || connector.currentUserRole === "admin";

  const setShared = useMutation(setConnectorSecretMutation());
  const clearShared = useMutation(clearConnectorSecretMutation());
  const setMine = useMutation(setMyConnectorCredentialMutation());
  const clearMine = useMutation(clearMyConnectorCredentialMutation());
  const saving = setShared.isPending || setMine.isPending;
  const removing = clearShared.isPending || clearMine.isPending;

  const refresh = () =>
    Promise.all(
      [getConnectorDetailQueryKey({ path }), listConnectorToolsQueryKey({ path }), listConnectorsQueryKey()].map(
        (queryKey) => queryClient.invalidateQueries({ queryKey }),
      ),
    );

  const save = async () => {
    try {
      const body = { value: value.trim() };
      await (shared ? setShared : setMine).mutateAsync({ path, body, throwOnError: true });
      toast.success(t("credentials.saved"));
      setEditing(false);
      setValue("");
      await refresh();
    } catch (error) {
      toast.error(errorMessage(error, t("credentials.saveFailed")));
    }
  };

  const remove = async () => {
    try {
      await (shared ? clearShared : clearMine).mutateAsync({ path, throwOnError: true });
      toast.success(t("credentials.removed"));
      await refresh();
    } catch (error) {
      toast.error(errorMessage(error, t("credentials.removeFailed")));
    }
  };

  // An integration's own connectors keep their credentials on the integration (Admin > Integrations).
  if (connector.authMode === "none" || (oauth && connector.isOrg)) return null;
  let title = shared ? t("credentials.sharedTitle") : t("credentials.mineTitle");
  if (oauth) title = t("credentials.clientSecretTitle");
  let body = shared ? t("credentials.sharedBody") : t("credentials.mineBody");
  if (oauth) body = t("credentials.clientSecretBody");
  let setLabel = t("credentials.addToken");
  if (isSet) setLabel = t("credentials.replace");
  else if (shared) setLabel = t("credentials.setValue");

  return (
    <Card>
      <section aria-label={title} className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <KeyRound size={16} aria-hidden className="text-content-secondary" />
          <Text variant="body">{title}</Text>
          <Badge variant={isSet ? "success" : "warning"}>
            {isSet ? t("credentials.set") : t("credentials.notSet")}
          </Badge>
        </div>
        <Text variant="body-sm" tone="secondary">
          {body}
        </Text>
        {!canSet && (
          <Text variant="caption" tone="tertiary">
            {oauth ? t("credentials.onlyAdminsClientSecret") : t("credentials.onlyAdmins")}
          </Text>
        )}
        {canSet && editing && (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (value.trim()) void save();
            }}
          >
            <FormField label={t("credentials.value")} htmlFor={inputId}>
              <Input
                id={inputId}
                type="password"
                autoComplete="off"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </FormField>
            <div className="flex gap-2">
              <Button type="submit" variant="primary" size="sm" loading={saving} disabled={!value.trim()}>
                {t("credentials.save")}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setEditing(false)} disabled={saving}>
                {t("credentials.cancel")}
              </Button>
            </div>
          </form>
        )}
        {canSet && !editing && (
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              {setLabel}
            </Button>
            {isSet && (
              <Button variant="ghost" size="sm" loading={removing} onClick={() => void remove()}>
                {t("credentials.remove")}
              </Button>
            )}
          </div>
        )}
      </section>
    </Card>
  );
}
